// TL cart recs loader
class TLCartRecommendations extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    this._loadedSeed = null;
    const { signal } = this._abort;

    document.addEventListener('cart:update', this._onCartUpdate, { signal });
    this.closest('cart-drawer-component')?.addEventListener('dialog:open', this._onDialogOpen, { signal });

    if (this._restore()) return;
    this._scheduleWarm();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._cancelWarm?.();
    this._cancelWarm = null;
    this._fetch?.abort();
  }

  _onDialogOpen = () => this._load();

  _onCartUpdate = () => {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      if (this.dataset.productId !== this._loadedSeed || !this.querySelector('[data-recs-items]')) {
        this._loadedSeed = null;
        this._load();
      }
    });
  };

  _scheduleWarm() {
    const warm = () => {
      this._cancelWarm = null;
      this._load();
    };
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(warm, { timeout: 1500 });
      this._cancelWarm = () => window.cancelIdleCallback(handle);
    } else {
      const handle = window.setTimeout(warm, 800);
      this._cancelWarm = () => window.clearTimeout(handle);
    }
  }

  _key() {
    return `tl:cdr:${this.dataset.productId}:${this.dataset.intent}:${this.dataset.limit}`;
  }

  // Session cache: public recs markup
  _read() {
    try {
      return sessionStorage.getItem(this._key());
    } catch {
      return null;
    }
  }

  _store(html) {
    try {
      sessionStorage.setItem(this._key(), html);
    } catch {
      /* private mode */
    }
  }

  _restore() {
    const cached = this._read();
    if (cached === null) return false;
    this._inject(cached);
    return true;
  }

  _inject(listHTML) {
    const target = this.querySelector('[data-recs-list]');
    if (!target) return;
    target.innerHTML = listHTML;
    this._loadedSeed = this.dataset.productId;
  }

  async _load() {
    const seed = this.dataset.productId;
    if (!seed || seed === this._loadedSeed) return;
    if (this._restore()) return;

    const limit = parseInt(this.dataset.limit, 10) || 4;
    const url = `${this.dataset.url}?limit=${limit + 1}&section_id=${this.dataset.sectionId}&product_id=${seed}&intent=${this.dataset.intent}`;

    this._fetch?.abort();
    this._fetch = new AbortController();
    try {
      const res = await fetch(url, { signal: this._fetch.signal });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = doc.querySelector('[data-recs-items]');
      let html = '';
      if (list) {
        list.querySelector(`li[data-product-id="${seed}"]`)?.remove();
        Array.from(list.children).slice(limit).forEach((li) => li.remove());
        if (list.children.length) html = list.outerHTML;
      }
      if (seed === this.dataset.productId) {
        this._inject(html);
        this._store(html);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('Cart recs error:', e);
    } finally {
      this._fetch = null;
    }
  }
}

if (!customElements.get('tl-cart-recommendations')) {
  customElements.define('tl-cart-recommendations', TLCartRecommendations);
}

// Pre-warm the current product's cart recs so the first add opens instantly
(() => {
  const seed = window.ShopifyAnalytics?.meta?.product?.id;
  const drawer = document.querySelector('cart-drawer-component[data-recs-url]');
  if (!seed || !drawer) return;

  const intent = drawer.dataset.recsIntent || 'related';
  const limit = parseInt(drawer.dataset.recsLimit, 10) || 4;
  const key = `tl:cdr:${seed}:${intent}:${limit}`;

  const warm = async () => {
    try {
      if (sessionStorage.getItem(key) !== null) return;
    } catch {
      return;
    }
    try {
      const url = `${drawer.dataset.recsUrl}?limit=${limit + 1}&section_id=${drawer.dataset.recsSectionId}&product_id=${seed}&intent=${intent}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = doc.querySelector('[data-recs-items]');
      let html = '';
      if (list) {
        list.querySelector(`li[data-product-id="${seed}"]`)?.remove();
        Array.from(list.children).slice(limit).forEach((li) => li.remove());
        if (list.children.length) html = list.outerHTML;
      }
      sessionStorage.setItem(key, html);
    } catch {
      /* ignore */
    }
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(warm, { timeout: 2000 });
  } else {
    window.setTimeout(warm, 1000);
  }
})();
